import {
  WSButton,
  WSButtons,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import {
  Currency,
  RecurringDocumentStatus,
  RecurringInvoice
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesRecurringInvoices } from "@query";
import { calculateRecurringLineItemsTotal } from "../../../../Invoices/utils";
import { selectorIsRecurringSeries } from "../../../../shared/selectors/selectorIsRecurringSeries";
import { selectorRecurrenceCadenceLabel } from "../../../../shared/selectors/selectorRecurrenceCadenceLabel";
import { selectorRecurringInvoiceActionLabels } from "../../../../shared/selectors/selectorRecurringInvoiceActionLabels";
import { selectorRemainingOccurrenceCount } from "../../../../shared/selectors/selectorRemainingOccurrenceCount";
import { getLocalStartDayByUTC } from "../../../../utils/dates";

type Props = {
  recurringInvoice: RecurringInvoice;
  onClose: () => void;
};

export const ModalDelete: React.FC<Props> = props => {
  const [deleteRecurringInvoice, deleteRecurringInvoiceMeta] =
    queriesRecurringInvoices.deleteRecurringInvoice.useMutation();
  const [cancelRecurringInvoice, cancelRecurringInvoiceMeta] =
    queriesRecurringInvoices.cancelRecurringInvoice.useMutation();

  const isCancel =
    props.recurringInvoice.status === RecurringDocumentStatus.ACTIVE;
  const isSeries = selectorIsRecurringSeries(props.recurringInvoice);
  const labels = selectorRecurringInvoiceActionLabels(props.recurringInvoice);

  const nextOccurrenceDate = props.recurringInvoice.nextOccurrenceDate;
  const amount = formatMoney(
    calculateRecurringLineItemsTotal(
      props.recurringInvoice.invoiceTemplate?.lineItems
    ),
    props.recurringInvoice.currency as Currency
  );
  const verb = isCancel ? "cancel" : "delete";
  const remainingCount = selectorRemainingOccurrenceCount(
    props.recurringInvoice
  );

  return (
    <>
      <WSText.Heading5 mb="M">
        {isCancel ? labels.cancel : `Delete ${labels.noun}`}
      </WSText.Heading5>
      {isSeries ? (
        <WSText mb="M">
          Are you sure you want to {verb} this series? It repeats{" "}
          {selectorRecurrenceCadenceLabel(
            props.recurringInvoice.schedule
          ).toLowerCase()}
          , and {remainingCount}{" "}
          {remainingCount === 1 ? "remaining invoice" : "remaining invoices"} of{" "}
          {amount} will not be sent.
        </WSText>
      ) : (
        <WSText mb="M">
          Are you sure you want to {verb} your{" "}
          {nextOccurrenceDate
            ? `${toWSDateString(
                getLocalStartDayByUTC(nextOccurrenceDate),
                "monthDayYear"
              )} invoice`
            : "invoice"}{" "}
          for {amount}?
        </WSText>
      )}
      <WSText mb="XL">
        This action can't be undone, once you {verb} a {labels.noun} you'll have
        to recreate it.
      </WSText>

      <ErrorAlert
        mb="XL"
        context="RecurringInvoiceAction"
        error={
          deleteRecurringInvoiceMeta.error || cancelRecurringInvoiceMeta.error
        }
      />

      <WSButtons format="modal">
        <WSButton
          destructive
          name="delete"
          onClick={() => {
            const recurringInvoiceId = props.recurringInvoice.id ?? "";

            if (isCancel) {
              cancelRecurringInvoice(
                { recurringInvoiceId },
                { onSuccess: props.onClose }
              );
            } else {
              deleteRecurringInvoice(
                { recurringInvoiceId },
                { onSuccess: props.onClose }
              );
            }
          }}
          loading={
            cancelRecurringInvoiceMeta.isLoading ||
            deleteRecurringInvoiceMeta.isLoading
          }
        >
          {isCancel ? labels.cancel : `Delete ${labels.noun}`}
        </WSButton>
        <WSButton.Secondary onClick={props.onClose}>
          {labels.keep}
        </WSButton.Secondary>
      </WSButtons>
    </>
  );
};
